import React, { useEffect, useState } from "react";
import { ReferenceTaskShape, TaskConfigParamOptions, TaskOwner } from "@eave/shared/types/api";
import {
  ContentSwitcher,
  Loading,
  Modal,
  Search,
  Switch,
  TableCell,
} from "carbon-components-react";
import { dueDateParamsToDisplayString } from "underwriter_dashboard/hurdle_editors/Tasks/helpers";
import {
  getTaskOptions,
  getReferenceTasks,
  createReferenceTask,
  updateReferenceTask,
  deleteReferenceTask,
} from "underwriter_dashboard/shared/Tasks/APIHelpers";
import { LoadingState } from "underwriter_dashboard/shared/types";

import BaseTable, { EditingState } from "web_modules/shared/BaseTable";

import TaskConfigModalForm from "./TaskConfigModalForm";

export interface ReferenceTaskFormShape extends ReferenceTaskShape {
  default_index_order: number; // current index_order differs from default_index_order as defined by `newTaskDefaultIndexOrder`
}

const conditionParams = (params: any) => {
  if (!params) {
    return null;
  }
  return Object.entries(params)
    .map(x => `${x[0]}: ${x[1]}`)
    .join(", ");
};

const RuleSet = (taskId: number, rules: any) => {
  if (!rules || !rules.operator) {
    return null;
  }

  const displayText = `When ${
    rules.operator === "AND" ? "all of" : "one of"
  } the following conditions are true`;

  return (
    <td key={taskId}>
      <p>{displayText}</p>
      <ol>
        {rules.conditions.map(condition => {
          return (
            <li key={condition.func_name}>
              * {condition.func_name} {conditionParams(condition.params)}
            </li>
          );
        })}
      </ol>
    </td>
  );
};

const defaultFormState = {
  include_in_queues: true,
  is_critical: false,
  pinned: false,
  blocks_loan_decision: false,
} as ReferenceTaskFormShape;

const searchTasks = (tasks: ReferenceTaskShape[], query: string): ReferenceTaskShape[] => {
  // Stringify text, lowercase both values, then compare
  return tasks.filter(t => {
    return JSON.stringify(t).toLowerCase().includes(query.toLowerCase());
  });
};

const TaskConfig = () => {
  const [tasks, setTasks] = useState<ReferenceTaskShape[]>([]);
  const [role, setRole] = useState<TaskOwner>(TaskOwner.Underwriter);
  const [loadingState, setLoadingState] = useState(LoadingState.ACTIVE);

  const [searchText, setSearchText] = useState("");

  // BaseTable
  const [formState, setFormState] = useState(defaultFormState);

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [deleteModalIsOpen, updateDeleteModalIsOpen] = useState(false);
  const [editingMode, setEditingMode] = useState(EditingState.INACTIVE);
  const [taskOptions, setTaskOptions] = useState<TaskConfigParamOptions>(null);

  const loadOptions = async () => {
    const options = await getTaskOptions();
    setTaskOptions(options as TaskConfigParamOptions);
  };

  const loadTasks = async () => {
    const t = await getReferenceTasks();
    setTasks(t);
  };

  useEffect(() => {
    setLoadingState(LoadingState.ACTIVE);

    loadTasks();
    loadOptions();

    setLoadingState(LoadingState.FINISHED);
  }, []);

  const resetForm = () => {
    setFormState(defaultFormState);
  };

  const tasksForRole = tasks.filter((task: ReferenceTaskShape) => task.owner === role);

  const dataFilteredBySearch =
    searchText === "" ? tasksForRole : searchTasks(tasksForRole, searchText);

  return (
    <div style={{ position: "relative" }}>
      <Loading active={loadingState === LoadingState.ACTIVE} />
      <Modal
        danger
        open={deleteModalIsOpen}
        modalHeading="Are you sure you'd like to delete this record?"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        onBlur={() => updateDeleteModalIsOpen(false)}
        onRequestClose={() => updateDeleteModalIsOpen(false)}
        onRequestSubmit={async () => {
          await deleteReferenceTask(formState.id);
          updateDeleteModalIsOpen(false);
          loadTasks();
        }}
      />

      {loadingState !== LoadingState.ACTIVE && taskOptions && (
        <TaskConfigModalForm
          isOpen={modalIsOpen}
          editingMode={editingMode}
          formState={formState}
          setFormState={setFormState}
          taskOptions={taskOptions}
          tasks={dataFilteredBySearch.sort((a, b) => {
            if (a.index_order < b.index_order) {
              return -1;
            }
            if (a.index_order > b.index_order) {
              return 1;
            }
            return 0;
          })}
          onRequestSubmit={async () => {
            if (editingMode === EditingState.CREATE) {
              await createReferenceTask(formState);
              resetForm();
              loadTasks();
            }
            if (editingMode === EditingState.UPDATE) {
              await updateReferenceTask(formState.id, formState);
              resetForm();
              loadTasks();
            }

            setModalIsOpen(false);
          }}
          onRequestClose={() => {
            resetForm();
            setModalIsOpen(false);
          }}
        />
      )}
      <div
        style={{
          position: "absolute",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          width: "100%",
          top: "-50px",
        }}
      >
        <p style={{ fontWeight: "bold" }}>Selected role &nbsp;</p>
        <ContentSwitcher onChange={e => setRole(e.name)} style={{ maxWidth: "40%" }}>
          <Switch name={TaskOwner.Underwriter} text="Underwriter" />
          <Switch name={TaskOwner.LMLender} text="Loan Manager" />
        </ContentSwitcher>
      </div>
      <div style={{ marginBottom: "1.5rem" }} />
      {/* NO-OP: Add search functionality */}
      <div style={{ width: "500px", marginBottom: "1rem" }}>
        <Search
          placeholder="Search by task name, stage, or type"
          labelText="Search tasks"
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
        />
      </div>
      {tasks && (
        <BaseTable
          title={`Task Logic: ${role}`}
          updateFormState={setFormState}
          disableBatch
          disableBatchEdit
          allowHeaderTextWrapping
          openModal={() => setModalIsOpen(true)}
          openDeleteModal={() => updateDeleteModalIsOpen(true)}
          updateEditingMode={setEditingMode}
          headerData={[
            {
              header: "Owner",
              key: "owner",
            },
            {
              header: "Category",
              key: "category",
            },
            {
              header: "Stage",
              key: "stage",
            },
            {
              header: "Name",
              key: "name",
            },
            {
              header: "Visibility Conditions",
              key: "visibility_conditions",
            },
            {
              header: "Completion Conditions",
              key: "completion_conditions",
            },
            {
              header: "Incl in queues",
              key: "include_in_queues",
            },
            {
              header: "Blocks decision",
              key: "blocks_loan_decision",
            },
            {
              header: "Critical?",
              key: "is_critical",
              style: { color: "red", fontWeight: "bold" },
            },
            {
              header: "Pinned?",
              key: "pinned",
              style: { color: "red", fontWeight: "bold" }, // TODO
            },
            {
              header: "Due Date Parameters",
              key: "due_date_params",
            },
          ]}
          rawRowData={dataFilteredBySearch}
          rowData={dataFilteredBySearch
            .sort((t: ReferenceTaskShape) => (t.pinned ? -1 : 1))
            .map((task: ReferenceTaskShape) => {
              return {
                ...task,
                id: `${task.id}`,
                lowOpacity: false,
                visibility_conditions: RuleSet(task.id, task.visibility_conditions),
                completion_conditions: RuleSet(task.id, task.completion_conditions),
                due_date_params:
                  task && task.due_date_params
                    ? dueDateParamsToDisplayString(task.due_date_params)
                    : "-",
                include_in_queues: task.include_in_queues ? "Yes" : "No",
                blocks_loan_decision: task.blocks_loan_decision ? "Yes" : "No",
                is_critical: (
                  <TableCell
                    key={`${task.id}-is-critical`}
                    style={
                      task.is_critical ? { color: "red", fontWeight: "bold" } : { opacity: 0.7 }
                    }
                  >
                    {task.is_critical ? "Yes" : "No"}
                  </TableCell>
                ),
                pinned: (
                  <TableCell
                    key={`${task.id}-pinned`}
                    style={task.pinned ? { color: "red", fontWeight: "bold" } : { opacity: 0.7 }}
                  >
                    {task.pinned ? "Yes" : "No"}
                  </TableCell>
                ),
              };
            })}
        />
      )}
    </div>
  );
};

export default TaskConfig;
